/**
 * Flattens a calibration, service, part, or labour item
 * into a UI-friendly structure.
 *
 * @param {Object} item - Item object returned from QWI API
 * @param {string} type - One of: 'calibration' | 'service' | 'part' | 'labour'
 * @returns {Object} Flattened item
 */
export const flattenItem = (item, type) => {
  const nested = item?.[type]
  return {
    ...item,
    [`${type}_id`]: nested?.id,
    name: nested?.name,
    price: item.cost_price,
    description: item?.description,
    calibration_center_id: nested?.calibration_center_id,
    itemNum: nested?.itemNum,
    category: nested?.category,
    is_defaulted: nested?.is_defaulted,
    is_active: nested?.is_active,
    calibration_created_at: nested?.created_at,
    calibration_updated_at: nested?.updated_at,
    category_id: item?.category?.id
  }
}

/**
 * Converts an array of items into a map keyed by itemNum,
 * and marks items as `checked` if their itemNum is selected.
 *
 * @param {Array} data - Array of items
 * @param {Array} selectedItems - Array of selected itemNum values
 * @returns {Object} Map of itemNum -> item with checked status
 */
export const transformToMap = (data = [], selectedItems = []) => {
  return data.reduce((map, item) => {
    map[item.itemNum] = {
      ...item,
      checked: selectedItems.includes(item.itemNum)
    }
    return map
  }, {})
}

/**
 * Product id of a document line (quote / work order QWI line), raw or flattened.
 * The line's own `id` is the QWI line id, not the product id, so it is never used here.
 *
 * @param {Object} line - QWI line, raw (`line.calibration.id`) or flattened (`line.calibration_id`)
 * @param {string} productType - One of: 'calibration' | 'service' | 'part' | 'labour'
 * @returns {number|string|undefined} Product id
 */
export const getLineProductId = (line, productType) => line?.[`${productType}_id`] ?? line?.[productType]?.id

/**
 * Builds the Manage drawer map from the loaded catalog and the document's existing lines.
 * Catalog items are ticked when their product id is on the document. Lines whose product is
 * not in the catalog (empty or different referral catalog, retired product) are added ticked,
 * so closing the drawer without changes never drops them (ISS-34).
 *
 * @param {Array} catalog - Catalog products (`id` is the product id)
 * @param {Array} selectedLines - Flattened document lines (see `flattenItem`)
 * @param {string} productType - One of: 'calibration' | 'service' | 'part' | 'labour'
 * @returns {Object} Map of key -> item with checked status; catalog items are keyed by itemNum
 */
export const buildSelectableMap = (catalog = [], selectedLines = [], productType) => {
  const selectedIds = new Set(
    selectedLines
      .map((line) => getLineProductId(line, productType))
      .filter((id) => id !== undefined && id !== null)
      .map(String)
  )

  const map = catalog.reduce((acc, item) => {
    acc[item.itemNum] = {
      ...item,
      checked: selectedIds.has(String(item.id))
    }
    return acc
  }, {})

  const listedIds = new Set(Object.values(map).map((item) => String(item.id)))
  selectedLines.forEach((line) => {
    const productId = getLineProductId(line, productType)
    if (productId === undefined || productId === null || listedIds.has(String(productId))) return
    listedIds.add(String(productId))
    // `id` must be the product id: it is what the update endpoints send.
    map[`line-${productId}`] = { ...line, id: productId, qwi_line_id: line.id, checked: true }
  })

  return map
}

export const formatItemList = (response) => {
  const map = {}
  if (response?.status === 200) {
    response.data.forEach((item) => {
      map[item.itemNum] = item
    })
  }
  return map
}

export const normalizeCustomItems = (items = []) =>
  items.map((item) => ({
    ...item,
    category_id: item.category?.id,
    price: item.cost_price
  }))

export const splitCustomItems = (items = []) => {
  const rebateItems = []
  const standardItems = []
  items.forEach((item) => {
    if (item.is_client_rebate_applied) {
      rebateItems.push(item)
    } else {
      standardItems.push(item)
    }
  })
  return { rebateItems, standardItems }
}
